import { useStyle } from "./style";
import { Card, CardContent, Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import GOMO_WIFI_MESH_6 from "@static/img/broadband/addons/GOMO_WIFI_MESH_6.png";
import { OfferPrice } from "@common/OfferPrice/OfferPrice";
import { AddonCounter } from "@common/BroadbandAddon/AddonCounter/AddonCounter";
import { useBroadbandAddon } from "@common/BroadbandAddon/useBroadbandAddon";
import { IEirSalesOfferAddon } from "@modules/eirSalesFacade/interfaces/models/IEirSalesOfferAddon";
import { ICartOffer } from "@features/cart/ICart";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { BUTTON_IDS } from "@features/dataLayer/buttonIds";
import { CardTag } from "@common/CardTag/CardTag";

interface IParams {
    addon: IEirSalesOfferAddon;
    isCompactLayout?: boolean;
    broadbandCartOffer: ICartOffer;
    isSidePanel?: boolean;
}

export const BroadbandAddon = (props: IParams) => {
    const { addon, isCompactLayout, broadbandCartOffer, isSidePanel } = props;
    const { code, description, totalPrice, maxQuantity } = addon;
    const { classes } = useStyle({ isCompactLayout });
    const { t } = useTranslation("home", { keyPrefix: "broadbandAddon" });
    const { handleAddAddon, handleRemoveAddon, isCounterVisible, counter, isLoading } = useBroadbandAddon({
        catalogCode: code,
        broadbandCartOffer,
        isSidePanel,
    });

    return (
        <div className={classes.container} data-testid={"broadband-addon-container"}>
            <CardTag isCompactLayout={isCompactLayout} label={t("optionalAddon")} />
            <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                    {!isCompactLayout && (
                        <Stack alignItems="center" direction="row" gap={2} justifyContent="flex-start">
                            <img alt={code} height={146} src={GOMO_WIFI_MESH_6} width={146} />
                            <Stack direction="column" flex={1} gap={1} maxWidth={400}>
                                <Typography color="primary" variant="h2">
                                    {description}
                                </Typography>
                                <Typography color="secondary" variant="large">
                                    <Trans i18nKey="broadbandAddon.addonsDescriptions.GOMO_WIFI_MESH_6" ns="home" />
                                </Typography>
                            </Stack>
                        </Stack>
                    )}
                    {isCompactLayout && (
                        <>
                            <Stack className={classes.cardHeader} direction="row" gap={2}>
                                <img alt={code} height={92} src={GOMO_WIFI_MESH_6} width={92} />
                                <Typography color="primary" variant="h2">
                                    {description}
                                </Typography>
                            </Stack>
                            <Typography color="secondary" variant="large">
                                <Trans i18nKey="broadbandAddon.addonsDescriptions.GOMO_WIFI_MESH_6" ns="home" />
                            </Typography>
                        </>
                    )}
                    <div className={classes.priceContainer}>
                        <OfferPrice price={totalPrice} priceFontSize={isCompactLayout ? "36px" : "42px"} />
                        {isCounterVisible && (
                            <AddonCounter
                                counter={counter}
                                handleAddAddon={handleAddAddon}
                                handleRemoveAddon={handleRemoveAddon}
                                isLoading={isLoading}
                                maxQuantity={maxQuantity}
                                showSubtitle
                            />
                        )}
                        {!isCounterVisible && (
                            <LabeledButton
                                className={classes.button}
                                color="primary"
                                disabled={isLoading}
                                id={
                                    isSidePanel
                                        ? BUTTON_IDS.MOBILE_FLOW.DRAWER_CTA.BOOST_MY_BROADBAND
                                        : BUTTON_IDS.BROADBAND_FLOW.ADD_TO_CART_CTA.BOOST_MY_BROADBAND
                                }
                                labelText={t("buyWifiBoost")}
                                onClick={handleAddAddon}
                            >
                                {t("buyWifiBoost")}
                            </LabeledButton>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
